import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import PublicCatalogCart from './components/PublicCatalogCart'
import { entrepreneurCatalogService } from '../../services/entrepreneurCatalogService'

const formatPrice = (value) => new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  maximumFractionDigits: 0
}).format(Number(value || 0))

const getCartStorageKey = (slug) => `entrepreneur-catalog-cart:/catalogo/${slug || ''}`

const loadCart = (slug) => {
  try {
    const stored = window.sessionStorage.getItem(getCartStorageKey(slug))
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

export default function PublicProductDetail() {
  const { slug, productId } = useParams()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const [selectedVideoId, setSelectedVideoId] = useState(null)
  const [selectedQuantity, setSelectedQuantity] = useState(1)
  const [cartOpen, setCartOpen] = useState(false)
  const [cartItems, setCartItems] = useState(() => loadCart(slug))

  useEffect(() => {
    setCartItems(loadCart(slug))
  }, [slug])

  useEffect(() => {
    try {
      window.sessionStorage.setItem(getCartStorageKey(slug), JSON.stringify(cartItems))
    } catch {}
  }, [cartItems, slug])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const result = await entrepreneurCatalogService.getPublicProduct(slug, productId)
        if (!cancelled) {
          setData(result)
          const primary = result?.product?.videos?.find((video) => video.isPrimary) || result?.product?.videos?.[0] || null
          setSelectedVideoId(primary?.id || null)
        }
      } catch {
        if (!cancelled) setData(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [slug, productId])

  const totalCartQuantity = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.quantity, 0),
    [cartItems]
  )

  const selectedVideo = data?.product?.videos?.find((video) => video.id === selectedVideoId) || data?.product?.videos?.[0] || null

  const addToCart = () => {
    if (!data?.product) return
    const qty = Math.max(1, Number(selectedQuantity || 1))
    setCartItems((current) => {
      const existing = current.find((item) => item.id === data.product.id)
      if (existing) {
        return current.map((item) => item.id === data.product.id ? { ...item, quantity: item.quantity + qty } : item)
      }

      return [
        ...current,
        {
          id: data.product.id,
          name: data.product.name,
          slug: data.product.slug,
          categoryName: data.product.categoryName || '',
          thumbnailUrl: selectedVideo?.thumbnailUrl || '',
          price: Number(data.product.price || 0),
          quantity: qty
        }
      ]
    })
    setCartOpen(true)
  }

  const changeCartQuantity = (id, nextQuantity) => {
    setCartItems((current) => {
      if (nextQuantity <= 0) return current.filter((item) => item.id !== id)
      return current.map((item) => item.id === id ? { ...item, quantity: nextQuantity } : item)
    })
  }

  if (loading) {
    return <div className="min-h-screen grid place-items-center text-slate-600">Cargando producto...</div>
  }

  if (!data) {
    return <div className="min-h-screen grid place-items-center text-slate-600">Este producto no esta disponible en el catalogo publico.</div>
  }

  return (
    <div className="min-h-screen p-3 grid gap-3 bg-slate-100 text-slate-900 xl:grid-cols-[minmax(0,1.08fr)_minmax(320px,.72fr)]">
      <section className="bg-slate-950 rounded-3xl border border-slate-800 shadow-xl overflow-hidden">
        <div className="p-3">
          {selectedVideo ? (
            <iframe
              src={selectedVideo.embedUrl || selectedVideo.videoUrl}
              loading="lazy"
              title={selectedVideo.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="w-full h-[min(52vh,560px)] rounded-2xl border-0 bg-black block"
            />
          ) : (
            <div className="min-h-[320px] grid place-items-center text-slate-300">Este producto no tiene videos publicados.</div>
          )}
        </div>

        <div className="grid [grid-template-columns:repeat(auto-fit,minmax(120px,1fr))] gap-2 px-3 pb-3">
          {data.product.videos.map((video) => (
            <button
              key={video.id}
              type="button"
              onClick={() => setSelectedVideoId(video.id)}
              className={`text-left rounded-2xl px-3 py-2 border ${selectedVideo?.id === video.id ? 'border-blue-300 bg-blue-900/20 text-white' : 'border-white/10 bg-white/5 text-white'}`}
            >
              <div className="text-[13px] font-bold leading-tight">{video.title}</div>
              <div className="mt-1 text-[11px] opacity-75">{video.sourceType}</div>
            </button>
          ))}
        </div>
      </section>

      <aside className="bg-white rounded-3xl border border-slate-200 shadow-xl overflow-hidden">
        <div className="p-5 grid gap-4">
          <div className="grid gap-3">
            <Link to={`/catalogo/${encodeURIComponent(data.profile.slug)}`} className="inline-flex items-center gap-2 text-sm font-bold text-slate-500">
              ← Volver al catalogo
            </Link>
            <div className="uppercase tracking-[0.18em] text-[11px] text-slate-500">Producto</div>
            <h1 className="text-[clamp(24px,3vw,36px)] leading-tight tracking-tight">
              {data.product.name}
            </h1>
            {data.product.categoryName && (
              <div className="inline-flex items-center w-fit rounded-full px-3 py-1 bg-slate-100 text-slate-600 text-xs font-bold">
                {data.product.categoryName}
              </div>
            )}
            <p className="text-sm leading-6 text-slate-500">
              {data.product.description || 'Explora el video y revisa la presentacion completa del producto.'}
            </p>
            <div className="text-3xl font-semibold text-slate-900">
              {formatPrice(data.product.price)}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="inline-flex items-center gap-2 border border-slate-200 rounded-full p-1 bg-white">
                <button type="button" className="w-8 h-8 rounded-full bg-slate-100 font-bold" onClick={() => setSelectedQuantity((current) => Math.max(1, current - 1))}>
                  -
                </button>
                <span className="min-w-6 text-center text-sm font-bold">{selectedQuantity}</span>
                <button type="button" className="w-8 h-8 rounded-full bg-slate-100 font-bold" onClick={() => setSelectedQuantity((current) => current + 1)}>
                  +
                </button>
              </div>
              <button type="button" className="inline-flex items-center justify-center min-h-10 px-4 rounded-full bg-blue-600 text-white text-sm font-bold" onClick={addToCart}>
                Agregar al carrito
              </button>
              <button type="button" className="inline-flex items-center justify-center min-h-10 px-4 rounded-full border border-slate-200 bg-white text-slate-700 text-sm font-bold" onClick={() => setCartOpen((current) => !current)}>
                {totalCartQuantity} en carrito
              </button>
            </div>
          </div>

          <div className="grid gap-2">
            <div className="uppercase tracking-[0.12em] text-[13px] text-slate-500">Negocio</div>
            <div className="text-base text-slate-900">{data.profile.displayName || data.profile.businessName}</div>
            <div className="text-sm leading-6 text-slate-500">{data.profile.tagline || data.profile.description || ''}</div>
          </div>

          <div className="border-t border-slate-200 pt-4 whitespace-pre-wrap text-sm leading-6 text-slate-700">
            {data.product.presentation || data.product.description || 'Sin presentacion adicional.'}
          </div>

          <div className="grid gap-1">
            <div className="uppercase tracking-[0.12em] text-[13px] text-slate-500">Videos</div>
            <div className="text-sm text-slate-500">{data.product.videos.length} video(s) disponible(s)</div>
          </div>
        </div>
      </aside>

      <PublicCatalogCart
        slug={data.profile.slug}
        contactPhone={data.profile.contactPhone || ''}
        contactName={data.profile.displayName || data.profile.businessName || ''}
        isOpen={cartOpen}
        items={cartItems}
        onClose={() => setCartOpen(false)}
        onChangeQuantity={changeCartQuantity}
        onClear={() => setCartItems([])}
      />
    </div>
  )
}
